/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getCluDeployModelService } from 'src/clu/core/services/deploy/cluDeployModelService';
import { CluTrainedModelStore } from 'src/clu/core/stores/CluTrainedModelStore';
import {
    deploymentAssignmentsSwaggerToResourceAssignmentsSwagger,
    resourceAssignmentSwaggersToRegionDeploymentStores
} from 'src/core/handlers/resourceAssignmentMappers';
import {
    ResourceAssignmentDto,
    ResourceAssignmentForRegionSwagger,
    ResourceMetadataSwagger
} from 'src/core/services/resourceAssignmentSwaggers';
import { CustomSkillDeploymentStore } from 'src/core/stores/CustomSkillDeploymentStore';
import { ResourceAssignmentStore } from 'src/core/stores/ResourceAssignmentStore';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { Region } from 'src/core/types/regionTypes';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { getNextAbleResults } from 'src/core/utils/batchPromises';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';

export type CluDeployModelHandlerGroup = ReturnType<typeof createCluDeployModelHandlerGroup>;

/**
 * Represents a handler group for clu model deployment.
 *
 * @param dependencies The handler group dependencies.
 */
export const createCluDeployModelHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { lifetime, rootStore } = dependencies;

    const deployCluModel = async ({
        context,
        deploymentName,
        trainedModelName,
        assignedResourceIds
    }: HandlerParams<{ trainedModelName: string; deploymentName: string; assignedResourceIds: string[] }>) => {
        const projectName = rootStore.currentCluProjectStore.name;

        const currentResourceId = rootStore.azureStore.session.csAccountStore?.path;
        const currentRegion = rootStore.azureStore.session.csAccountStore?.region;

        const defaultResourceAssignment: ResourceAssignmentForRegionSwagger = {
            region: currentRegion,
            azureResourceId: currentResourceId
        };

        await getCluDeployModelService({ context }).deploy({
            projectName,
            deploymentName,
            trainedModelName,
            assignedResourceIds,
            operationHandle: {
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                retryStrategy: res => !lifetime.isDisposed && res.data.status !== 'succeeded' && res.data.status !== 'failed',
                handleCall: async data => {
                    if (lifetime.isDisposed) {
                        return;
                    }

                    if (data.status === 'succeeded') {
                        const deployment = await getCluDeployModelService({ context }).getDeployment(projectName, deploymentName);
                        const existingDeployment = rootStore.currentCluProjectStore.deploymentsStoreHouse.findById(deploymentName);

                        rootStore.currentCluProjectStore.deploymentsStoreHouse[Boolean(existingDeployment) ? 'replace' : 'add'](
                            new CustomSkillDeploymentStore(deploymentName, {
                                deploymentName,
                                modelName: trainedModelName,
                                trainingConfigVersion: deployment.modelTrainingConfigVersion,
                                lastTrainedDateTime: deployment.lastTrainedDateTime && new Date(deployment.lastTrainedDateTime),
                                lastDeployedDateTime: deployment.lastDeployedDateTime && new Date(deployment.lastDeployedDateTime),
                                expirationDate: deployment.deploymentExpirationDate && new Date(deployment.deploymentExpirationDate),
                                assignedResources: (
                                    deploymentAssignmentsSwaggerToResourceAssignmentsSwagger(deployment.assignedResources) ?? []
                                ).concat(defaultResourceAssignment)
                            })
                        );
                    }

                    if (data.status === 'failed') {
                        throw new Error(data.errors?.[0]?.message);
                    }
                }
            }
        });
    };

    const swapCluDeployments = async ({
        targetDeploymentName,
        sourceDeploymentName
    }: HandlerParams<{ targetDeploymentName: string; sourceDeploymentName: string }>) => {
        const { name: projectName, deploymentsStoreHouse, trainedModelStoreHouse } = rootStore.currentCluProjectStore;

        await getCluDeployModelService().swapDeployments({
            projectName,
            targetDeploymentName,
            sourceDeploymentName,
            operationHandle: {
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                retryStrategy: res => !lifetime.isDisposed && res.data.status !== 'succeeded' && res.data.status !== 'failed',
                handleCall: data => {
                    if (data.status === 'failed') {
                        throw new Error(data.errors?.[0]?.message);
                    }
                }
            }
        });

        await getCluDeployments({ projectName, deploymentsStoreHouse, trainedModelStoreHouse });
    };

    const deleteCLuDeployment = async ({ deploymentName }: HandlerParams<{ deploymentName: string }>) => {
        const { name: projectName, deploymentsStoreHouse } = rootStore.currentCluProjectStore;

        await getCluDeployModelService().deleteDeployment({
            projectName,
            deploymentName,
            operationHandle: {
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                retryStrategy: res => !lifetime.isDisposed && res.data.status !== 'succeeded' && res.data.status !== 'failed',
                handleCall: data => {
                    if (data.status === 'failed') {
                        throw new Error(data.errors?.[0]?.message);
                    }
                }
            }
        });

        deploymentsStoreHouse.removeById(deploymentName);
    };

    const getCluDeployments = async ({
        projectName,
        deploymentsStoreHouse,
        trainedModelStoreHouse
    }: HandlerParams<{
        projectName: string;
        deploymentsStoreHouse: ResourceStoreHouse<CustomSkillDeploymentStore>;
        trainedModelStoreHouse?: ResourceStoreHouse<CluTrainedModelStore>;
    }>) => {
        deploymentsStoreHouse.tracker.setState('running');

        let trialNumber = 0;
        // Wait for trained models to be loaded.
        while (trainedModelStoreHouse && !trainedModelStoreHouse.tracker.isCompleted) {
            await new Promise(res => setTimeout(res, OperationDelayStrategies.exponentialDelayStrategy(null, trialNumber++)));
        }

        const currentResourceId = rootStore.azureStore.session.csAccountStore?.path;
        const currentRegion = rootStore.azureStore.session.csAccountStore?.region;

        const defaultResourceAssignment: ResourceAssignmentForRegionSwagger = {
            region: currentRegion,
            azureResourceId: currentResourceId
        };

        const deploymentsSwagger = await getNextAbleResults(getCluDeployModelService().getDeployments, 'value', [projectName]);

        deploymentsStoreHouse.setItems(
            deploymentsSwagger.map(
                d =>
                    new CustomSkillDeploymentStore(d.deploymentName, {
                        deploymentName: d.deploymentName,
                        modelName: trainedModelStoreHouse?.findById(d.modelId)?.name,
                        lastTrainedDateTime: d.lastTrainedDateTime && new Date(d.lastTrainedDateTime),
                        lastDeployedDateTime: d.lastDeployedDateTime && new Date(d.lastDeployedDateTime),
                        expirationDate: d.deploymentExpirationDate && new Date(d.deploymentExpirationDate),
                        assignedResources: (deploymentAssignmentsSwaggerToResourceAssignmentsSwagger(d.assignedResources) ?? []).concat(
                            defaultResourceAssignment
                        )
                    })
            )
        );

        deploymentsStoreHouse.tracker.setState('completed');
    };

    const getCluProjectAssignedResources = async () => {
        const { name: projectName, resourceAssignmentsStoreHouse } = rootStore.currentCluProjectStore;

        resourceAssignmentsStoreHouse.tracker.setState('running');

        const currentResourceId = rootStore.azureStore.session.csAccountStore?.path;
        const currentRegion = rootStore.azureStore.session.csAccountStore?.region;
        const currentResourceName = rootStore.azureStore.session.csAccountStore?.name;

        const currentSubscription = rootStore.azureStore.subscriptionStoreHouse.findById(rootStore.azureStore.session.subscriptionId);

        const getSubscriptionName = (subscriptionId: string) => rootStore.azureStore.subscriptionStoreHouse.findById(subscriptionId)?.name;

        const assignedResources = await getNextAbleResults(
            (nextLink: string) => getCluDeployModelService().getAssignedResources({ nextLink, projectName }),
            'value'
        );

        assignedResources.push({
            region: currentRegion,
            azureResourceId: currentResourceId
        });

        const regionDeploymentItems: ResourceAssignmentStore[] = resourceAssignmentSwaggersToRegionDeploymentStores({
            assignedResources,
            getSubscriptionName,
            defaultRegionInfo: {
                region: currentRegion,
                resourceName: currentResourceName,
                azureResourceId: currentResourceId,
                subscriptionId: currentSubscription?.id,
                subscriptionName: currentSubscription?.name
            },
            supportedRegions: LuisConstants.Clu.PredictionAllowedRegions
        });

        resourceAssignmentsStoreHouse.setItems(regionDeploymentItems);
        resourceAssignmentsStoreHouse.tracker.setState('completed');
    };

    const addCluProjectResourceAssignment = async ({
        assignmentData
    }: HandlerParams<{
        assignmentData: ResourceAssignmentDto;
    }>) => {
        const { name: projectName, resourceAssignmentsStoreHouse } = rootStore.currentCluProjectStore;

        const assignedResources: ResourceMetadataSwagger[] = [
            {
                region: assignmentData.id,
                customDomain: assignmentData.customDomain,
                azureResourceId: assignmentData.azureResourceId
            }
        ];

        await getCluDeployModelService().addResourceAssignments({
            projectName,
            assignedResources,
            operationHandle: {
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                retryStrategy: res => !lifetime.isDisposed && res.data.status !== 'succeeded' && res.data.status !== 'failed',
                handleCall: data => {
                    if (data.status === 'failed') {
                        throw new Error(data.errors?.[0]?.message);
                    }
                }
            }
        });

        resourceAssignmentsStoreHouse.replace({
            isDefaultResource: false,
            id: assignmentData.id as Region,
            regionName: assignmentData.regionName,
            resourceName: assignmentData.resourceName,
            subscriptionId: assignmentData.subscriptionId,
            azureResourceId: assignmentData.azureResourceId,
            subscriptionName: assignmentData.subscriptionName
        });
    };

    const updateCluProjectResourceAssignment = async ({
        oldResourceId,
        assignmentData
    }: HandlerParams<{
        oldResourceId: string;
        assignmentData: ResourceAssignmentDto;
    }>) => {
        const { name: projectName, resourceAssignmentsStoreHouse } = rootStore.currentCluProjectStore;

        if (oldResourceId) {
            await getCluDeployModelService().removeResourceAssignments({
                projectName,
                assignedResources: [oldResourceId],
                operationHandle: {
                    timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                    retryStrategy: res => !lifetime.isDisposed && res.data.status !== 'succeeded' && res.data.status !== 'failed',
                    handleCall: data => {
                        if (data.status === 'failed') {
                            throw new Error(data.errors?.[0]?.message);
                        }
                    }
                }
            });
        }

        const assignedResources: ResourceMetadataSwagger[] = [
            {
                region: assignmentData.id,
                customDomain: assignmentData.customDomain,
                azureResourceId: assignmentData.azureResourceId
            }
        ];

        await getCluDeployModelService().addResourceAssignments({
            projectName,
            assignedResources,
            operationHandle: {
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                retryStrategy: res => !lifetime.isDisposed && res.data.status !== 'succeeded' && res.data.status !== 'failed',
                handleCall: data => {
                    if (data.status === 'failed') {
                        throw new Error(data.errors?.[0]?.message);
                    }
                }
            }
        });

        resourceAssignmentsStoreHouse.replace({
            isDefaultResource: false,
            id: assignmentData.id as Region,
            regionName: assignmentData.regionName,
            resourceName: assignmentData.resourceName,
            subscriptionId: assignmentData.subscriptionId,
            azureResourceId: assignmentData.azureResourceId,
            subscriptionName: assignmentData.subscriptionName
        });
    };

    const deleteCluProjectResourceAssignment = async ({
        assignmentData
    }: HandlerParams<{
        assignmentData: Partial<ResourceAssignmentStore>;
    }>) => {
        const {
            name: projectName,
            resourceAssignmentsStoreHouse,
            deploymentsStoreHouse,
            trainedModelStoreHouse
        } = rootStore.currentCluProjectStore;

        await getCluDeployModelService().removeResourceAssignments({
            projectName,
            assignedResources: [assignmentData.azureResourceId],
            operationHandle: {
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                retryStrategy: res => !lifetime.isDisposed && res.data.status !== 'succeeded' && res.data.status !== 'failed',
                handleCall: data => {
                    if (data.status === 'failed') {
                        throw new Error(data.errors?.[0]?.message);
                    }
                }
            }
        });

        resourceAssignmentsStoreHouse.replace({
            resourceName: null,
            subscriptionId: null,
            azureResourceId: null,
            subscriptionName: null,
            isDefaultResource: false,
            id: assignmentData.id as Region,
            regionName: assignmentData.regionName
        });

        await getCluDeployments({ projectName, deploymentsStoreHouse, trainedModelStoreHouse });
    };

    return {
        deployCluModel,
        getCluDeployments,
        deleteCLuDeployment,
        swapCluDeployments,
        getCluProjectAssignedResources,
        addCluProjectResourceAssignment,
        updateCluProjectResourceAssignment,
        deleteCluProjectResourceAssignment
    };
};
